import React, { useEffect, useState, useRef } from "react";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import { useToast } from "../../context/ToastContext";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import RegisterFailureApi from "../../services/FailureApi";
import { Calendar } from "primereact/calendar";
import OperationApi from "../../services/OperationApi";
import ProductionUnitApi from "../../services/ProductionUnitApi";
import AreaApi from "../../services/AreaApi";
import LineApi from "../../services/LineApi";
import SublineApi from "../../services/SublineApi";
import { useAuth } from "../../context/AuthContext";

const FastResponse: React.FC = () => {
  const { plantId } = useAuth();
  const unidadeRef = useRef<any>(null);
  const areaRef = useRef<any>(null);
  const linhaRef = useRef<any>(null);
  const sublinhaRef = useRef<any>(null);
  const operacaoRef = useRef<any>(null);
  const causaRef = useRef<any>(null);
  const efeitoRef = useRef<any>(null);
  const descricaoRef = useRef<any>(null);
  const produtoRef = useRef<any>(null);
  const tipoFalhaRef = useRef<any>(null);
  const dataFalhaRef = useRef<any>(null);
  const severidadeRef = useRef<any>(null);
  const ocorrenciaRef = useRef<any>(null);
  const detectabilidadeRef = useRef<any>(null);
  const fileUploadRef = useRef<FileUpload>(null); // comment: ref para controlar FileUpload
  const { displayToast } = useToast();
  const navigate = useNavigate();
  const { t: translate } = useTranslation("form");
  const [unidades, setUnidades] = useState([]);
  const [areas, setAreas] = useState([]);
  const [linhas, setLinhas] = useState([]);
  const [sublinhas, setSublinhas] = useState([]);
  const [operacoes, setOperacoes] = useState([]);

  const [unidade, setUnidade] = useState<{ id: string; name: string } | null>(
    null,
  );
  const [area, setArea] = useState<{ id: string; name: string } | null>(null);
  const [linha, setLinha] = useState<{ id: string; name: string } | null>(null);
  const [sublinha, setSublinha] = useState<{ id: string; name: string } | null>(
    null,
  );
  const [operacao, setOperacao] = useState<{ id: string; name: string } | null>(
    null,
  );

  const [descricao, setDescricao] = useState("");
  const [efeito, setEfeito] = useState("");
  const [causas, setCausas] = useState([]);
  const [causa, setCausa] = useState<{ id: string; name: string } | null>(null);

  const [severidade, setSeveridade] = useState<number | null>(null);
  const [ocorrencia, setOcorrencia] = useState<number | null>(null);
  const [detectabilidade, setDetectabilidade] = useState<number | null>(null);
  const [rpn, setRpn] = useState("");
  const [files, setFiles] = useState<any[]>([]);

  const [selectedProduto, setSelectedProduto] = useState(null);
  const [selectedTipoFalha, setSelectedTipoFalha] = useState(null);
  const [dataFalha, setDataFalha] = useState<Date | null | undefined>(null);

  const produtos = [
    { label: "55268912A", value: "55268912A" },
    { label: "44387214C", value: "44387214C" },
    { label: "33621800A", value: "33621800A" },
  ];

  const tiposFalha = [
    { label: "Interna", value: "Interna" },
    { label: "Externa", value: "Externa" },
  ];

  useEffect(() => {
    const fetchInitialData = async (): Promise<void> => {
      try {
        const [unidadesResponse] = await Promise.all([
          ProductionUnitApi.GetProductUnitByPlant(plantId),
          OperationApi.GetAllOperationTypes(),
        ]);

        setUnidades(unidadesResponse.data);
        setUnidade(null);
        setArea(null);
        setLinha(null);
        setSublinha(null);
      } catch (error) {
        console.error("Erro ao carregar dados iniciais:", error);
        displayToast("error", "Erro ao buscar dados", "", false, 3000);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    if (unidade) {
      AreaApi.GetAreaByProductionUnit(unidade.id).then((response) =>
        setAreas(response.data),
      );
      setArea(null);
      setLinha(null);
      setSublinha(null);
    }
  }, [unidade]);

  useEffect(() => {
    if (area) {
      LineApi.GetLinesByArea(area.id).then((response) =>
        setLinhas(response.data),
      );
      setLinha(null);
      setSublinha(null);
    }
  }, [area]);

  useEffect(() => {
    if (linha) {
      SublineApi.GetSublineByLine(linha.id).then((response) =>
        setSublinhas(response.data),
      );
      setSublinha(null);
    }
  }, [linha]);

  useEffect(() => {
    const fetchCausas = async (): Promise<void> => {
      try {
        const response = await RegisterFailureApi.GetAllCauses();
        setCausas(response.data);
      } catch (error) {
        console.error("Erro ao buscar causas", error);
        displayToast("error", "Erro ao buscar causas", "", false, 3000);
      }
    };

    fetchCausas();
  }, []);

  useEffect(() => {
    const fetchOperations = async (): Promise<void> => {
      if (unidade && area && linha && sublinha) {
        try {
          const response = await OperationApi.GetByFilter(
            [plantId],
            1,
            1000,
            "name",
            "asc",
            undefined,
            undefined,
            undefined,
            undefined,
            [unidade.id],
            [area.id],
            [linha.id],
            [sublinha.id],
          );

          const ops = Array.isArray(response.data.operations)
            ? response.data.operations
            : [];
          setOperacoes(ops);
        } catch (error) {
          console.error("Erro ao buscar operações filtradas", error);
        }
      }
    };

    fetchOperations();
  }, [unidade, area, linha, sublinha]);

  useEffect(() => {
    const s = parseInt(severidade?.toString() || "0");
    const o = parseInt(ocorrencia?.toString() || "0");
    const d = parseInt(detectabilidade?.toString() || "0");

    if (!isNaN(s) && !isNaN(o) && !isNaN(d)) {
      setRpn((s * o * d).toString());
    } else {
      setRpn("");
    }
  }, [severidade, ocorrencia, detectabilidade]);

  const onDropdownKeyDown: any = (
    e: React.KeyboardEvent,
    ref: React.RefObject<any>,
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      ref.current?.element?.click();
    }
  };

  const handleCancel = (): void => {
    confirmDialog({
      message: translate("cancelCreationMessage"),
      header: translate("confirmation"),
      icon: "pi pi-exclamation-triangle",
      acceptLabel: translate("Yes"),
      rejectLabel: translate("No"),
      accept: () => {
        navigate("/fmeas");
      },
      reject: () => {},
    });
  };

  const handleSave = async (): Promise<void> => {
    if (
      !operacao ||
      !descricao ||
      !efeito ||
      !causa?.id ||
      !severidade ||
      !ocorrencia ||
      !detectabilidade
    ) {
      displayToast("warn", "Aviso", "Preencha todos os campos obrigatórios.");
      return;
    }

    const formData = new FormData();
    formData.append("Name", descricao);
    formData.append("Description", descricao);
    formData.append("Effect", efeito);
    formData.append("Severity", String(severidade));
    formData.append("Occurrence", String(ocorrencia));
    formData.append("Detectability", String(detectabilidade));
    formData.append("OperationId", operacao.id);
    formData.append("CauseId", causa.id);
    formData.append("PlantId", plantId);
    formData.append("ProductionUnitId", unidade?.id ?? "");
    formData.append("AreaId", area?.id ?? "");
    formData.append("LineId", linha?.id ?? "");
    formData.append("SubLineId", sublinha?.id ?? "");
    files.forEach((file) => {
      formData.append("Files", file);
    });

    try {
      await RegisterFailureApi.Create(formData);
      displayToast(
        "success",
        "Sucesso",
        "Falha potencial cadastrada com sucesso!",
        false,
        3000,
      );
      navigate("/fmeas");
    } catch (error) {
      console.error(error);
      displayToast(
        "error",
        "Erro ao cadastrar falha",
        "Ocorreu um erro ao tentar salvar a falha.",
        false,
        3000,
      );
    }
  };

  const requiredStyle = {
    color: "red",
    marginLeft: "0.25rem",
    fontWeight: 600,
  };
  // permite adicionar novos arquivos sem substituir
  const onFileSelect: any = (e: any) => {
    const newFiles = e.files;
    setFiles((prev) => [...prev, ...newFiles]); // comment: adiciona arquivos sem sobrescrever
    // limpa seleção do componente para permitir novo choose
    fileUploadRef.current?.clear(); // comment: limpa UI para reabrir seletor
  };
  // remove arquivo específico
  const removeFile: any = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index)); // comment: remove arquivo da lista
  };

  return (
    <div>
      <h2>Fast Response</h2>
      <div className="grid">
        <div className="col-12">
          <div className="grid">
            <div className="col-6 pb-0">
              <label
                className={`text-sm ${unidade ? "opacity-100" : "opacity-0"}`}
              >
                {" "}
                {translate("productionUnit")}{" "}
                {unidade && <span style={requiredStyle}>*</span>}{" "}
              </label>
              <Dropdown
                value={unidade}
                options={unidades}
                onChange={(e) => {
                  setUnidade(e.value);
                  setTimeout(() => {
                    areaRef.current?.focus();
                    areaRef.current?.element?.click();
                  }, 0);
                }}
                optionLabel="name"
                placeholder={`${translate("selectUnit")} *`}
                className="w-full"
                ref={unidadeRef}
                onKeyDown={(e) => onDropdownKeyDown(e, unidadeRef)}
                tabIndex={0}
              />
            </div>
            <div className="col-6 pb-0">
              <label
                className={`text-sm ${area ? "opacity-100" : "opacity-0"}`}
              >
                {translate("area")}{" "}
                {area && <span style={requiredStyle}>*</span>}
              </label>
              <Dropdown
                value={area}
                options={areas}
                onChange={(e) => {
                  setArea(e.value);
                  setTimeout(() => {
                    linhaRef.current?.focus();
                    linhaRef.current?.element?.click();
                  }, 0);
                }}
                optionLabel="name"
                placeholder={`${translate("selectArea")} *`}
                className="w-full"
                disabled={!unidade}
                ref={areaRef}
                onKeyDown={(e) => onDropdownKeyDown(e, areaRef)}
                tabIndex={0}
              />
            </div>
            <div className="col-6 pt-1 pb-0">
              <label
                className={`text-sm ${linha ? "opacity-100" : "opacity-0"}`}
              >
                {translate("line")}{" "}
                {linha && <span style={requiredStyle}>*</span>}
              </label>
              <Dropdown
                value={linha}
                options={linhas}
                onChange={(e) => {
                  setLinha(e.value);
                  setTimeout(() => {
                    sublinhaRef.current?.focus();
                    sublinhaRef.current?.element?.click();
                  }, 0);
                }}
                optionLabel="name"
                placeholder={`${translate("selectLine")} *`}
                className="w-full"
                disabled={!area}
                ref={linhaRef}
                onKeyDown={(e) => onDropdownKeyDown(e, linhaRef)}
                tabIndex={0}
              />
            </div>
            <div className="col-6 pt-1 pb-0">
              <label
                className={`text-sm ${sublinha ? "opacity-100" : "opacity-0"}`}
              >
                {translate("subline")}{" "}
                {sublinha && <span style={requiredStyle}>*</span>}
              </label>
              <Dropdown
                value={sublinha}
                options={sublinhas}
                onChange={(e) => {
                  setSublinha(e.value);
                  setTimeout(() => {
                    operacaoRef.current?.focus();
                  }, 0);
                }}
                optionLabel="name"
                placeholder={`${translate("selectSubline")} *`}
                className="w-full"
                disabled={!linha}
                ref={sublinhaRef}
                onKeyDown={(e) => onDropdownKeyDown(e, sublinhaRef)}
                tabIndex={0}
              />
            </div>
            <div className="col-12">
              <label
                className={`text-sm ${operacao ? "opacity-100" : "opacity-0"}`}
              >
                {translate("operation")}{" "}
                {sublinha && <span style={requiredStyle}>*</span>}
              </label>
              <Dropdown
                value={operacao}
                options={operacoes}
                onChange={(e) => {
                  setOperacao(e.value);
                  setTimeout(() => {
                    causaRef.current?.focus();
                  }, 0);
                }}
                optionLabel="name"
                placeholder={`${translate("selectOperation")} *`}
                className="w-full"
                disabled={!sublinha}
                ref={operacaoRef}
                onKeyDown={(e) => onDropdownKeyDown(e, operacaoRef)}
                tabIndex={0}
              />
            </div>
            <div className="col-12 mt-3">
              <div className="grid">
                <div className="col-12 md:col-6">
                  <label
                    className={`text-sm ${causa ? "opacity-100" : "opacity-0"}`}
                  >
                    {translate("cause")}{" "}
                    {causa && <span style={requiredStyle}>*</span>}
                  </label>
                  <Dropdown
                    value={causa}
                    options={causas}
                    onChange={(e) => {
                      setCausa(e.value);
                      setTimeout(() => {
                        efeitoRef.current?.focus();
                      }, 0);
                    }}
                    optionLabel="name"
                    placeholder={`${translate("selectCause")} *`}
                    className="w-full"
                    ref={causaRef}
                    onKeyDown={(e) => onDropdownKeyDown(e, causaRef)}
                    tabIndex={0}
                  />
                </div>
                <div className="col-12 md:col-6">
                  <label
                    className={`text-sm ${efeito ? "opacity-100" : "opacity-0"}`}
                  >
                    {translate("effect")}{" "}
                    {efeito && <span style={requiredStyle}>*</span>}
                  </label>
                  <InputTextarea
                    value={efeito}
                    onChange={(e) => setEfeito(e.target.value)}
                    placeholder={`${translate("effect")} *`}
                    maxLength={50}
                    rows={1}
                    className="w-full"
                    ref={efeitoRef}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        descricaoRef.current?.focus();
                      }
                    }}
                    tabIndex={0}
                  />
                </div>
                <div className="col-12 md:col-12">
                  <label
                    className={`text-sm ${descricao ? "opacity-100" : "opacity-0"}`}
                  >
                    {" "}
                    {translate("Description")}{" "}
                    {descricao && <span style={requiredStyle}>*</span>}
                  </label>
                  <InputTextarea
                    value={descricao}
                    onChange={(e) => setDescricao(e.target.value)}
                    placeholder={`${translate("Description")} *`}
                    rows={3}
                    maxLength={500}
                    className="w-full"
                    ref={descricaoRef}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        produtoRef.current?.focus();
                      }
                    }}
                    tabIndex={0}
                  />
                </div>
                <div className="col-12 md:col-4">
                  <label
                    className={`text-sm ${selectedProduto ? "opacity-100" : "opacity-0"}`}
                  >
                    {" "}
                    {"Produto"}{" "}
                    {selectedProduto && <span style={requiredStyle}>*</span>}
                  </label>
                  <Dropdown
                    id="produto"
                    value={selectedProduto}
                    options={produtos}
                    onChange={(e) => {
                      setSelectedProduto(e.value);
                      setTimeout(() => {
                        tipoFalhaRef.current?.focus();
                      }, 0);
                    }}
                    placeholder="Produto *"
                    className="w-full"
                    ref={produtoRef}
                    onKeyDown={(e) => onDropdownKeyDown(e, produtoRef)}
                    tabIndex={0}
                  />
                </div>

                <div className="col-12 md:col-4">
                  <label
                    className={`text-sm ${selectedTipoFalha ? "opacity-100" : "opacity-0"}`}
                  >
                    {" "}
                    {"Tipo"}{" "}
                    {selectedTipoFalha && <span style={requiredStyle}>*</span>}
                  </label>
                  <Dropdown
                    id="tipoFalha"
                    value={selectedTipoFalha}
                    options={tiposFalha}
                    onChange={(e) => {
                      setSelectedTipoFalha(e.value);
                      setTimeout(() => {
                        dataFalhaRef.current?.focus();
                      }, 0);
                    }}
                    placeholder="Tipo Falha *"
                    className="w-full"
                    ref={tipoFalhaRef}
                    onKeyDown={(e) => onDropdownKeyDown(e, tipoFalhaRef)}
                    tabIndex={0}
                  />
                </div>

                <div className="col-12 md:col-4">
                  <label
                    className={`text-sm ${dataFalha ? "opacity-100" : "opacity-0"}`}
                  >
                    {" "}
                    {"Data Falha"}{" "}
                    {dataFalha && <span style={requiredStyle}>*</span>}
                  </label>
                  <Calendar
                    id="dataFalha"
                    value={dataFalha}
                    onChange={(e) => {
                      setDataFalha(e.value);
                      setTimeout(() => {
                        severidadeRef.current?.focus();
                      }, 0);
                    }}
                    showIcon
                    showTime
                    placeholder="Data Falha *"
                    className="w-full"
                    ref={dataFalhaRef}
                    tabIndex={0}
                  />
                </div>
                <div className="col-4 pt-0">
                  <label
                    className={`text-sm ${severidade ? "opacity-100" : "opacity-0"}`}
                  >
                    {translate("severity") ?? "Severidade"}{" "}
                    {severidade && <span style={requiredStyle}>*</span>}
                  </label>
                  <InputNumber
                    value={severidade}
                    onValueChange={(e) => setSeveridade(e.value ?? null)}
                    placeholder={`${translate("severity") ?? "Severidade (1-10)"} *`}
                    min={1}
                    max={10}
                    className="w-full"
                    ref={severidadeRef}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        ocorrenciaRef.current?.focus();
                      }
                    }}
                    tabIndex={0}
                  />
                </div>
                <div className="col-4 pt-0 pb-0">
                  <label
                    className={`text-sm ${ocorrencia ? "opacity-100" : "opacity-0"}`}
                  >
                    {translate("occurrence") ?? "Ocorrência"}{" "}
                    {ocorrencia && <span style={requiredStyle}>*</span>}
                  </label>
                  <InputNumber
                    value={ocorrencia}
                    onValueChange={(e) => setOcorrencia(e.value ?? null)}
                    placeholder={`${translate("occurrence") ?? "Ocorrência (1-10)"} *`}
                    min={1}
                    max={10}
                    className="w-full"
                    ref={ocorrenciaRef}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        detectabilidadeRef.current?.focus();
                      }
                    }}
                    tabIndex={0}
                  />
                </div>
                <div className="col-4 pt-0 pb-0">
                  <label
                    className={`text-sm ${detectabilidade ? "opacity-100" : "opacity-0"}`}
                  >
                    {translate("detectability") ?? "Detectabilidade"}{" "}
                    {detectabilidade && <span style={requiredStyle}>*</span>}
                  </label>
                  <InputNumber
                    value={detectabilidade}
                    onValueChange={(e) => setDetectabilidade(e.value ?? null)}
                    placeholder={`${translate("detectability") ?? "Detectabilidade (1-10)"} *`}
                    min={1}
                    max={10}
                    className="w-full"
                    ref={detectabilidadeRef}
                  />
                </div>
                <div className="col-4 pt-0 pb-0">
                  <label className="text-sm opacity-100">RPN</label>
                  <InputText id="rpn" value={rpn} readOnly className="w-full" />
                  <small className="text-xs mt-1 text-gray-500">
                    {translate("rpn")}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 mt-3">
          <FileUpload
            mode="basic"
            ref={fileUploadRef}
            customUpload
            auto
            chooseLabel={translate("attachFiles")}
            multiple
            onSelect={onFileSelect}
          />
        </div>

        {files.length > 0 && (
          <div className="col-12">
            <small className="text-gray-600">
              {translate("selectedFiles")}:
            </small>
            <ul className="mt-2 pl-3">
              {files.map((file, index) => (
                <li key={index} className="text-sm flex align-items-center">
                  <span className="mr-2">{file.name}</span>
                  <Button
                    icon="pi pi-times"
                    className="p-button-text p-button-danger p-button-sm"
                    onClick={() => removeFile(index)} // comment: botão para remover
                    aria-label={translate("removeFile")}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="col-12 flex justify-between mt-4">
          <Button
            type="button"
            label={translate("Cancel")}
            className="p-button-danger"
            onClick={handleCancel}
          />
          <div className="ml-auto">
            <Button
              type="submit"
              label={translate("register")}
              onClick={handleSave}
              icon="pi pi-check"
              className="p-button-success gap-2"
            />
          </div>
        </div>
      </div>
      <ConfirmDialog />
    </div>
  );
};

export default FastResponse;
